import { useQueryClient } from '@tanstack/react-query';
import Pusher from 'pusher-js';
import { useEffect } from 'react';

import usePusher from '~hooks/usePusher';

import {
	BUSINESS_CHANNEL_PREFIX,
	ENTITLEMENTS_CHANGED_EVENT,
	ENTITLEMENTS_QUERY_KEY,
} from '~constants/entitlements';

const SUBSCRIPTION_SUCCEEDED_EVENT = 'pusher:subscription_succeeded';

/**
 * Reads entitlements again whenever CentsBE reports a change to the business (COS-4956), so a grant or
 * revocation shows without a new login. The event carries only the business id: the read decides
 * what this user may see.
 *
 * Every successful subscription reads again too: a change made before the first one (the initial
 * read can finish first) or while disconnected (pusher-js subscribes again on reconnect) sent its
 * event to nobody.
 */
export function useEntitlementsUpdates(businessId: number): void {
	const pusherClient = usePusher() as Pusher | undefined;
	const queryClient = useQueryClient();

	useEffect(() => {
		if (!pusherClient) {
			return undefined;
		}

		const queryKey = [ENTITLEMENTS_QUERY_KEY];
		// A read still in flight may have been answered before the change, and with no data cached yet
		// an invalidation would join it rather than start a new one: cancel it first.
		const readAgain = (): void => {
			queryClient
				.cancelQueries({ queryKey })
				.then(() => queryClient.invalidateQueries({ queryKey }));
		};

		const channel = pusherClient.subscribe(`${BUSINESS_CHANNEL_PREFIX}${businessId}`);
		channel.bind(ENTITLEMENTS_CHANGED_EVENT, readAgain);
		channel.bind(SUBSCRIPTION_SUCCEEDED_EVENT, readAgain);

		return () => {
			channel.unbind(ENTITLEMENTS_CHANGED_EVENT);
			channel.unbind(SUBSCRIPTION_SUCCEEDED_EVENT);
			// Through the client, so the channel leaves its registry and is not subscribed again on reconnect.
			pusherClient.unsubscribe(channel.name);
		};
	}, [pusherClient, queryClient, businessId]);
}
